/*
  rbc-gallery-index.js — the index head above the store's own collection grid on the De Niro flagship template.
  Section: sections/rbc-gallery-index-head.liquid. Contract: BUILD-CONTRACT.md §5 (this is the "index head").

  1. Type tabs are real links (filter.p.product_type); with JS they go through the theme's <collection-provider>,
     so the grid swaps in place: no jump to the top (noScroll), no stacked history (replace), the wall's hash kept.
  2. Follows the wall: on a non-init `rbc:gallery:state` whose disc changed, the tab marked data-follow (Tall boots)
     is narrowed to that discipline with the Discipline filter — only if that filter exists on the storefront
     (the section renders [data-gi-disc] when it is in collection.filters). From All, a discipline moves the index to
     the follow tab (only tall boots have a discipline). From another tab, nothing moves; the follow tab's link
     carries the choice for later.
  3. If the reader changes the Discipline filter in the grid's own filter drawer or removes its chip, the wall is
     told (`rbc:gallery:set`); its answering state is recognised as an echo, so the two never ping-pong.
  4. Keeps the tab counts true (live filter counts; counts inside a discipline only for the tab that follows it)
     and hides active-filter chips the tabs and the note already show. (Carrying filters across a sort change made
     from a never-opened drawer is the provider's job: collection-provider.js _getUrlParams.)
  5. The grid's cards read like the wall's labels: "Achille 01" / "from £620" instead of "De Niro Achille 01" /
     "From £620 GBP" (cardText). Only text nodes change — links, alt text and the link's accessible name keep the full
     title. Applied before the reader can see a card: to the server-rendered grid when this deferred script runs (before
     DOMContentLoaded; the grid is far below the fold), and to the provider's new document inside `collection:swap`,
     which fires BEFORE the swap (captured on window, so it runs ahead of every other listener and needs no
     preventDefault). Tabs, pagination, sort and Back/Forward all swap through that event. A MutationObserver on the
     grid is the safety net. Idempotent: provider documents are cached and swapped in again.
  The provider lets only its latest request publish and swap, so nothing here waits for a swap: tabs, note and
  hrefs follow the URL at once; counts follow the swapped doc or this section's own render; dimmed counts settle
  when the last count request ends, whatever its outcome.
*/
(() => {
  if (window.rbcGalleryIndex) return;

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const same = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
  const norm = q => Array.from(q).map(([k, v]) => k + '=' + v).sort().join('&');
  const toPath = q => { const s = q.toString(); return location.pathname + (s ? '?' + s : ''); };
  const params = () => new URLSearchParams(location.search);

  const S = {
    wallDisc: null,   // the disc the wall last announced (seeded from the URL / hash)
    asked: null,      // a disc we asked the wall for: its answering state is an echo, not a new choice
    urlDisc: '',      // the Discipline values in the URL as this script last left them
    counts: new Map(),// base key (URL without type / discipline / sort / page) -> count per tab, outside any discipline
    fcounts: new Map(),// follow-tab link (with a carried discipline) -> its count
    fetching: null,   // the count request in flight (its query may be '' — the bare collection — so null means none)
    ffetching: null,
    announce: false,
    heard: false      // the wall has announced at least once
  };

  const root = () => $('[data-gi]');
  const provider = (r = root()) => {
    const sec = r && r.closest('.shopify-section');
    const next = sec && sec.nextElementSibling;
    return next ? $('collection-provider', next) : null;
  };
  const ready = p => !!p && typeof p._onFilterChange === 'function';

  function conf(r) {
    if (r._gi) return r._gi;
    let disc = null;
    const d = $('[data-gi-disc]', r);
    if (d) { try { disc = JSON.parse(d.textContent); } catch (e) { disc = null; } }
    return (r._gi = {
      typeParam: r.dataset.typeParam,
      discParam: r.dataset.discParam,
      disc, // [{label, value}] when the Discipline filter exists, else null
      tabs: $$('[data-gi-tab]', r).map(a => ({
        a, all: a.hasAttribute('data-all'), follow: a.hasAttribute('data-follow'),
        values: (() => { try { return JSON.parse(a.dataset.values || '[]'); } catch (e) { return []; } })()
      }))
    });
  }

  const discValue = (c, disc) => {
    if (!disc || !c.disc) return '';
    const m = c.disc.find(d => same(d.label, disc) || same(d.value, disc));
    return m ? m.value : '';
  };
  const discLabel = (c, value) => { const m = c.disc && c.disc.find(d => d.value === value); return m ? m.label : value; };
  const urlDiscs = (c, p) => (c.disc ? p.getAll(c.discParam) : []);

  // a Discipline with no type in the URL (?filter.p.m.rbc.category=Dressage) is the follow tab: only tall boots have one
  function activeTab(c, p) {
    const types = p.getAll(c.typeParam);
    if (!types.length && urlDiscs(c, p).length) { const F = c.tabs.find(t => t.follow); if (F) return F; }
    return c.tabs.find(t => (t.all ? !types.length : types.length === t.values.length && t.values.every(v => types.includes(v)))) || null;
  }

  // the discipline the follow tab carries: the URL's, else the wall's
  function carried(c, p) {
    const inUrl = urlDiscs(c, p);
    if (inUrl.length) return inUrl;
    const v = discValue(c, S.wallDisc);
    return v ? [v] : [];
  }

  function tabQuery(c, t, p) {
    const q = new URLSearchParams(p);
    q.delete(c.typeParam); q.delete(c.discParam); q.delete('page');
    t.values.forEach(v => q.append(c.typeParam, v));
    if (t.follow) carried(c, p).forEach(v => q.append(c.discParam, v));
    return q;
  }

  function render(r, c) {
    const p = params();
    const cur = activeTab(c, p);
    c.tabs.forEach(t => {
      t.a.setAttribute('href', toPath(tabQuery(c, t, p)));
      if (t === cur) t.a.setAttribute('aria-current', 'true'); else t.a.removeAttribute('aria-current');
    });
    followCount(r, c, p);
    const note = $('[data-gi-note]', r);
    if (note && c.disc) {
      const vals = urlDiscs(c, p);
      note.hidden = vals.length !== 1;
      if (vals.length === 1) {
        $('[data-gi-note-text]', note).textContent = (note.dataset.template || '').replace('[discipline]', discLabel(c, vals[0]));
        $('[data-gi-clear]', note).setAttribute('href', toPath(clearQuery(c, p)));
      }
    }
  }

  // the Discipline in the URL changed without us (the reader removed its chip, or Clear all): tell the wall
  function syncWall(c) {
    if (!c.disc) return;
    const vals = urlDiscs(c, params());
    if (vals.join('|') === S.urlDisc) return;
    S.urlDisc = vals.join('|');
    const disc = vals.length === 1 ? discLabel(c, vals[0]) : '';
    if (same(disc, S.wallDisc)) return;
    S.asked = disc;
    S.wallDisc = disc; // this is the state now; the wall's answer is an echo
    document.dispatchEvent(new CustomEvent('rbc:gallery:set', { detail: { disc } }));
  }

  // the Discipline cleared; with no type in the URL that would be All, but the link promises all tall boots
  function clearQuery(c, p) {
    const q = new URLSearchParams(p); q.delete(c.discParam); q.delete('page');
    const F = c.tabs.find(t => t.follow);
    if (F && !q.getAll(c.typeParam).length) F.values.forEach(v => q.append(c.typeParam, v));
    return q;
  }
  // the URL already says what the wall says
  function agrees(c, disc) {
    if (!c.disc) return true;
    const u = urlDiscs(c, params());
    if (!disc) return !u.length;
    return u.length === 1 && (same(discLabel(c, u[0]), disc) || same(u[0], disc));
  }

  async function go(r, c, q, opts = {}) {
    if (!customElements.get('collection-provider')) await customElements.whenDefined('collection-provider');
    const p = provider(r);
    if (!ready(p)) return false;
    S.urlDisc = urlDiscs(c, q).join('|');
    S.announce = !!opts.announce;
    p._onFilterChange({ url: toPath(q), noScroll: true, replace: true, keepHash: true });
    render(r, c); // the provider has already written the URL; the tabs flip now, the grid when it arrives
    return true;
  }

  // the wall changed its discipline
  function follow(r, c, disc) {
    if (!c.disc) { render(r, c); return; } // no Discipline filter on this storefront: no narrowing, no note, no params
    const v = discValue(c, disc);
    const p = params();
    const types = p.getAll(c.typeParam);
    const cur = activeTab(c, p);
    const F = c.tabs.find(t => t.follow);
    const q = new URLSearchParams(p); q.delete('page');
    let apply = false;
    if (!F || cur === F || (!cur && types.some(x => F.values.includes(x)))) apply = true;
    else if (!types.length) {
      if (v) { F.values.forEach(x => q.append(c.typeParam, x)); apply = true; }
      else if (p.has(c.discParam)) apply = true;
    }
    if (apply) {
      q.delete(c.discParam);
      if (v) q.append(c.discParam, v);
      else if (F && cur === F && !types.length) F.values.forEach(x => q.append(c.typeParam, x)); // a discipline-only link: back to all tall boots
    }
    if (apply && norm(q) !== norm((() => { const b = new URLSearchParams(p); b.delete('page'); return b; })())) go(r, c, q);
    else render(r, c);
  }

  /* ---- counts ---- */
  function baseKey(c, p) {
    const q = new URLSearchParams(p);
    [c.typeParam, c.discParam, 'sort_by', 'page'].forEach(k => q.delete(k));
    return norm(q);
  }
  function docCounts(c, doc) {
    if (!doc || !doc.querySelectorAll) return null;
    const m = new Map();
    const roots = [doc].concat($$('template', doc).map(t => t.content));
    roots.forEach(rt => $$('input[name="' + c.typeParam + '"]', rt).forEach(i => {
      if (m.has(i.value)) return;
      const n = i.closest('label') && $('[class~="#collection-filter-item-counter"]', i.closest('label'));
      if (n) m.set(i.value, parseInt(n.textContent, 10) || 0);
    }));
    return m.size ? m : null;
  }
  function paint(c, arr) {
    c.tabs.forEach((t, i) => { if (arr[i] != null) { const n = $('[data-gi-n]', t.a); if (n) n.textContent = arr[i]; t.a.removeAttribute('data-pending'); } });
  }
  const pend = (c, which) => c.tabs.forEach((t, i) => { if (which(t, i)) t.a.setAttribute('data-pending', ''); });
  // a count request that failed or was overtaken paints nothing: once none is in flight, nothing may stay dimmed
  const settle = c => { if (S.fetching === null && S.ffetching === null) c.tabs.forEach(t => t.a.removeAttribute('data-pending')); };
  // ask this section for its counts under the params q (the head renders its own counts in Liquid)
  async function fetchCounts(r, c, q, key) {
    const f = new URLSearchParams(q); f.delete('page');
    const once = norm(f);
    if (S.fetching === once) return;
    S.fetching = once;
    try {
      f.set('section_id', r.dataset.sectionId);
      const res = await fetch(location.pathname + '?' + f.toString());
      if (!res.ok) return;
      const d = new DOMParser().parseFromString(await res.text(), 'text/html');
      const arr = $$('[data-gi-tab] [data-gi-n]', d).map(n => (n.textContent.trim() === '' ? null : +n.textContent));
      if (arr.length !== c.tabs.length) return;
      const asked = urlDiscs(c, q).length > 0;
      if (!asked) S.counts.set(key, arr);
      const now = params();
      if (baseKey(c, now) !== key) return; // the reader has moved on
      const on = urlDiscs(c, now).length > 0;
      paint(c, arr.map((n, i) => (on && !asked && c.tabs[i].follow ? null : n)));
    } catch (e) { /* counts stay as they were */ } finally { if (S.fetching === once) S.fetching = null; settle(c); }
  }
  // the follow tab carries the wall's discipline while another tab is open: its number must be the one its link leads to
  async function followCount(r, c, p) {
    const i = c.tabs.findIndex(t => t.follow);
    if (i < 0 || !S.heard || urlDiscs(c, p).length) return;
    if (!carried(c, p).length) { // nothing carried any more: back to the plain count
      const base = S.counts.get(baseKey(c, p));
      if (base && base[i] != null) paint(c, c.tabs.map((t, k) => (k === i ? base[i] : null)));
      return;
    }
    const q = tabQuery(c, c.tabs[i], p);
    const key = norm(q);
    const paintIt = n => { const now = params(); if (!urlDiscs(c, now).length && norm(tabQuery(c, c.tabs[i], now)) === key) paint(c, c.tabs.map((t, k) => (k === i ? n : null))); };
    if (S.fcounts.has(key)) { paintIt(S.fcounts.get(key)); return; }
    pend(c, (t, k) => k === i);
    if (S.ffetching === key) return;
    S.ffetching = key;
    try {
      q.set('section_id', r.dataset.sectionId);
      const res = await fetch(location.pathname + '?' + q.toString());
      if (!res.ok) return;
      const d = new DOMParser().parseFromString(await res.text(), 'text/html');
      const n = $$('[data-gi-tab] [data-gi-n]', d)[i];
      if (!n || n.textContent.trim() === '') return;
      S.fcounts.set(key, +n.textContent);
      paintIt(+n.textContent);
    } catch (e) { /* the number stays as it was */ } finally { if (S.ffetching === key) S.ffetching = null; settle(c); }
  }

  function counts(r, c, doc) {
    const p = params();
    const on = urlDiscs(c, p).length > 0;
    const key = baseKey(c, p);
    const m = docCounts(c, doc);
    if (!m) { // the grid's reply carries no Product type inputs (e.g. a one-value filter the theme skips): ask this section
      pend(c, () => true);
      fetchCounts(r, c, p, key);
      if (on) { const q = new URLSearchParams(p); q.delete(c.discParam); fetchCounts(r, c, q, key); }
      return;
    }
    const sum = t => (t.all ? Array.from(m.values()) : t.values.map(v => m.get(v) || 0)).reduce((a, b) => a + b, 0);
    if (!on) { const arr = c.tabs.map(sum); S.counts.set(key, arr); paint(c, arr); return; }
    const base = S.counts.get(key);
    paint(c, c.tabs.map((t, i) => (t.follow ? sum(t) : base ? base[i] : null)));
    if (!base) { pend(c, t => !t.follow); const q = new URLSearchParams(p); q.delete(c.discParam); fetchCounts(r, c, q, key); }
  }

  /* ---- the grid's cards: the wall's words ---- */
  const escRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const textNodes = el => { const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT); const a = []; while (w.nextNode()) a.push(w.currentNode); return a; };
  // the settings a card was formatted with; a card carries them so a later change of settings can be noticed
  const cardSig = r => ['strip', 'moneyCode', 'from', 'fromAs'].map(k => (r.dataset[k] || '').trim()).join('|');
  function cardText(r, scope) {
    if (!r || !scope || !scope.querySelectorAll) return;
    const sig = cardSig(r);
    const strip = (r.dataset.strip || '').trim();
    const code = (r.dataset.moneyCode || '').trim();
    const from = (r.dataset.from || '').trim();
    const fromAs = (r.dataset.fromAs || '').trim();
    const reCode = code ? new RegExp('\\s*\\b' + escRe(code) + '\\b', 'g') : null;
    const doc = scope.ownerDocument || scope;
    $$('[class~="#product-card"]', scope).forEach(card => {
      if (card.hasAttribute('data-gi-card')) return;
      card.setAttribute('data-gi-card', sig);
      // the name: "De Niro Achille 01" -> "Achille 01" (the brand is the page)
      const h = $('[class~="#product-card-title"]', card);
      if (h && strip && h.children.length === 0) {
        const t = h.textContent.trim();
        if (t.length > strip.length + 1 && t.slice(0, strip.length + 1).toLowerCase() === strip.toLowerCase() + ' ') h.textContent = t.slice(strip.length + 1);
      }
      const pr = $('[class~="#product-card-price"]', card);
      if (!pr) return;
      // the price: the shop's own currency code goes ("£620 GBP" -> "£620", "£323.80 GBP" -> "£323.80"); everything
      // else — pence, sale and compare-at prices, other markets' codes — is left exactly as the store wrote it
      if (reCode) textNodes(pr).forEach(n => { const v = n.nodeValue.replace(reCode, ''); if (v !== n.nodeValue) n.nodeValue = v; });
      // "From £620" -> "from £620", the word set small and smoke as on the wall
      if (from && fromAs) {
        const v = $('[class~="#price-item"][class~="@regular"] [class~="#price-value"]', pr);
        const n = v && textNodes(v).find(x => x.nodeValue.trim() !== '');
        if (n && n.nodeValue.trimStart().startsWith(from)) {
          const rest = n.nodeValue.trimStart().slice(from.length);
          const w = doc.createElement('small');
          w.className = 'rbc-gi-from';
          w.textContent = fromAs;
          n.nodeValue = rest.startsWith(' ') ? rest : ' ' + rest;
          n.parentNode.insertBefore(w, n);
        }
      }
    });
  }

  // Theme editor: this section re-renders with new card settings while the grid (another section) keeps cards written
  // with the old ones, and so do the provider's cached documents. Written words cannot be un-written: fetch the grid afresh.
  function refreshStaleCards(r) {
    const p = provider(r);
    if (!p) return;
    const sig = cardSig(r);
    // the provider remembers the settings its cached documents were written with — the grid may be empty when they change
    const changed = p._giSig !== undefined && p._giSig !== sig;
    p._giSig = sig;
    const stale = $$('[data-gi-card]', p).some(card => card.getAttribute('data-gi-card') !== sig);
    if (!changed && !stale) return;
    p.cache = {};
    if (stale && p._onFilterChange) p._onFilterChange({ url: location.pathname + location.search, replace: true, noScroll: true, keepHash: true });
  }

  /* ---- the grid's chips: the tabs and the note already show type and discipline ---- */
  function tidyChips(r, c) {
    const p = provider(r);
    if (!p) return;
    const cur = params();
    const tab = activeTab(c, cur);
    const notFilters = q => { ['page', 'sort_by'].forEach(k => q.delete(k)); return q; };
    notFilters(cur);
    const chips = $$('collection-active-filter[remove-url]', p);
    let shown = 0;
    chips.forEach(ch => {
      if (ch.matches('[class~="@clear"]')) return;
      let rm;
      try { rm = notFilters(new URL(ch.getAttribute('remove-url'), location.origin).searchParams); } catch (e) { return; }
      const gone = Array.from(cur).filter(([k, v]) => !rm.getAll(k).includes(v)).map(([k]) => k);
      const dup = gone.length === 1 && ((gone[0] === c.typeParam && tab && !tab.all) || (c.disc && gone[0] === c.discParam && urlDiscs(c, cur).length === 1));
      ch.classList.toggle('rbc-gi-dup', dup);
      if (!dup) shown++;
    });
    chips.forEach(ch => { if (ch.matches('[class~="@clear"]')) ch.classList.toggle('rbc-gi-dup', shown < 2); });
    const box = chips[0] && chips[0].parentElement;
    if (box) box.classList.toggle('rbc-gi-empty', shown === 0);
  }

  function seedWall(c, p) {
    const u = urlDiscs(c, p);
    if (u.length === 1) return discLabel(c, u[0]);
    const h = decodeURIComponent(location.hash.slice(1)).split('-')[0];
    if (!h || h === 'decorated' || h === 'plain') return '';
    if (c.disc ? c.disc.some(d => same(d.label, h)) : /^(jumping|dressage|polo)$/i.test(h)) return h;
    return '';
  }

  let mo = null;
  function watch(r, c) {
    const p = provider(r);
    if (!p || p._giWatched) return;
    p._giWatched = true;
    if (mo) mo.disconnect();
    let q = 0;
    // cards: at once (a MutationObserver callback runs before the next paint); chips: once per frame
    mo = new MutationObserver(() => { const rr = root(); if (rr) cardText(rr, provider(rr)); cancelAnimationFrame(q); q = requestAnimationFrame(() => { const r2 = root(); if (r2) tidyChips(r2, conf(r2)); }); });
    mo.observe(p, { childList: true, subtree: true });
  }

  function init(r) {
    if (!r || r._giInit) return;
    r._giInit = true;
    const c = conf(r);
    const p = params();
    S.urlDisc = urlDiscs(c, p).join('|');
    if (S.wallDisc === null) S.wallDisc = seedWall(c, p);
    if (urlDiscs(c, p).length) {
      const q = new URLSearchParams(p); q.delete(c.discParam);
      fetchCounts(r, c, q, baseKey(c, p));
    } else {
      S.counts.set(baseKey(c, p), c.tabs.map(t => { const n = $('[data-gi-n]', t.a); return n && n.textContent.trim() !== '' ? +n.textContent : null; }));
    }
    render(r, c);
    cardText(r, provider(r));
    refreshStaleCards(r);
    tidyChips(r, c);
    watch(r, c);
  }

  // A PURE deep link names a wall state and nothing else: no type, no discipline and no page in the query. Only then
  // does the index follow the wall at start-up; an explicit query is the reader's (or the link's) own choice.
  function pureDeepLink(c, disc) {
    const p0 = params();
    return !!disc && !p0.has(c.discParam) && !p0.has(c.typeParam) && !p0.has('page');
  }

  /* ---- events ---- */
  document.addEventListener('click', e => {
    const t = e.target.closest && e.target.closest('[data-gi-tab], [data-gi-clear]');
    if (!t || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const r = t.closest('[data-gi]');
    if (!r || !ready(provider(r))) return; // the grid is not live yet: let the link navigate
    const c = conf(r);
    e.preventDefault();
    if (t.hasAttribute('data-gi-tab')) {
      if (t.getAttribute('aria-current') === 'true') return;
      syncWall(c);
      const tab = c.tabs.find(x => x.a === t);
      // "All" is a deliberate choice of everything: the wall goes to All with it, so its hash no longer names a
      // discipline and a reload does not narrow the grid again.
      if (tab && tab.all && !same(S.wallDisc, '')) {
        S.asked = '';
        S.wallDisc = '';
        document.dispatchEvent(new CustomEvent('rbc:gallery:set', { detail: { disc: '' } }));
      }
      go(r, c, tab ? tabQuery(c, tab, params()) : new URL(t.getAttribute('href'), location.origin).searchParams, { announce: true });
      return;
    }
    // "Show all tall boots": ask the wall to show all, and clear the grid's narrowing now (works with no wall too)
    const q = clearQuery(c, params());
    if (!same(S.wallDisc, '')) {
      S.asked = '';
      S.wallDisc = '';
      document.dispatchEvent(new CustomEvent('rbc:gallery:set', { detail: { disc: '' } }));
    }
    go(r, c, q, { announce: true });
    const cur = activeTab(c, params());
    if (cur) cur.a.focus({ preventScroll: true }); // the note (and the focused link) is gone
  });

  document.addEventListener('rbc:gallery:state', e => {
    const d = e.detail || {};
    const disc = d.disc || '';
    const echo = S.asked !== null && same(disc, S.asked);
    S.asked = null;
    const changed = S.wallDisc === null || !same(disc, S.wallDisc);
    S.wallDisc = disc;
    S.heard = true;
    const r = root();
    if (!r) return;
    const c = conf(r);
    // init: a deep link (#dressage, #jumping-decorated) re-hangs the wall; the index follows unless the URL already agrees
    // Only a PURE deep link is followed: the wall has a discipline and the URL carries no grid choice of its own (no
    // type, no discipline). An explicit query — a type tab, or a discipline the wall cannot show, such as Polo — is the
    // reader's (or the link's) choice and is left alone; start-up never clears a filter.
    if (d.init) {
      if (pureDeepLink(c, disc) && !agrees(c, disc)) follow(r, c, disc); else render(r, c);
      return;
    }
    // Back / Forward restored the wall: the URL of the entry returned to is the authority (the provider re-renders the
    // grid from it). Reflect it; never follow, which would rewrite that entry.
    if (d.fromHistory) { render(r, c); return; }
    if (echo || !changed) { render(r, c); return; }
    follow(r, c, disc);
  });

  // before every swap: the provider's new document gets the wall's words, so the cards arrive already right
  window.addEventListener('collection:swap', e => {
    const r = root();
    if (r && e.target === provider(r) && e.target.doc) cardText(r, e.target.doc);
  }, true);

  document.addEventListener('collection:swap', e => {
    const r = root();
    if (!r || e.target !== provider(r)) return;
    const c = conf(r);
    syncWall(c);
    const doc = e.target.doc; // set by the provider before it dispatches this event
    counts(r, c, doc);
    render(r, c);
    if (S.announce) {
      const s = doc && doc.querySelector('[id$="-counter"]');
      const live = $('[data-gi-live]', r);
      if (s && live) live.textContent = s.textContent.trim().replace(/\s+/g, ' ');
    }
    S.announce = false;
  });

  document.addEventListener('shopify:section:load', () => { const r = root(); if (r) { const p = provider(r); if (p) p._giWatched = false; init(r); } });

  window.addEventListener('load', () => {
    if (S.heard) return;
    S.heard = true;
    const r = root();
    if (!r) return;
    const c = conf(r);
    if (pureDeepLink(c, S.wallDisc) && !agrees(c, S.wallDisc)) follow(r, c, S.wallDisc); else render(r, c);
  }, { once: true });

  window.rbcGalleryIndex = { state: S, cardText };
  init(root());
})();
